/**
 * Picker de MODELO HSM (v8 Etapa 3b), reusado pelo composer de janela fechada
 * e pelo dialog "Nova conversa": select de modelo (GET /whatsapp/templates,
 * cache 5min) + preview do body com os placeholders {{1}}..{{n}} renderizados
 * como inputs inline (preview vivo). Emite onChange(null) enquanto o modelo
 * não está escolhido ou algum parâmetro está vazio.
 *
 * Spec 025: os modelos são POR NÚMERO — o da Blip tem os da conta da Blip, o
 * da Meta os da conta da Meta. Com `phoneNumberId`, o select mostra só os do
 * número que vai mandar; se a lista desse número não carregou, a tela diz
 * isso (e oferece tentar de novo) em vez de "Nenhum modelo disponível".
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TEMPLATE_PLACEHOLDER_RE, templateParamCount } from '@erp/shared';
import { Label, Select } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import {
  templateFailureMessage,
  templatesOfNumber,
  whatsappTemplatesOptions,
  type WhatsappHsmTemplate,
} from './chats-queries';
import type { ChatTemplateBody } from './chats-mutations';
import { errorMessage } from './chats-format';

export type { ChatTemplateBody };

const templateKey = (t: WhatsappHsmTemplate) => `${t.name}\u0000${t.language}`;

export function TemplatePicker({
  onChange,
  disabled,
  idPrefix = 'tpl',
  className,
  phoneNumberId,
}: {
  /** null enquanto incompleto; corpo pronto para POST quando válido */
  onChange: (value: ChatTemplateBody | null) => void;
  disabled?: boolean;
  /** prefixo dos ids dos controles (evita colisão entre instâncias) */
  idPrefix?: string;
  className?: string;
  /**
   * o número que vai mandar (phone_number_id do canal): só os modelos dele.
   * null = o canal ainda não foi escolhido; undefined = sem filtro.
   */
  phoneNumberId?: string | null;
}) {
  const templatesQ = useQuery(whatsappTemplatesOptions());
  const noChannel = phoneNumberId === null;
  const { templates, failures } = templatesOfNumber(templatesQ.data, phoneNumberId ?? null);

  const [key, setKey] = useState('');
  const [params, setParams] = useState<string[]>([]);
  const selected = templates.find((t) => templateKey(t) === key) ?? null;

  const emit = (tpl: WhatsappHsmTemplate | null, nextParams: string[]) => {
    if (!tpl || nextParams.some((p) => p.trim() === '')) {
      onChange(null);
      return;
    }
    onChange({ name: tpl.name, language: tpl.language, params: nextParams.map((p) => p.trim()) });
  };

  const selectTemplate = (nextKey: string) => {
    setKey(nextKey);
    const tpl = templates.find((t) => templateKey(t) === nextKey) ?? null;
    // Conta com o helper COMPARTILHADO (mesma gramática do servidor — aceita
    // '{{ 1 }}' com espaços), garantindo um input para cada param exigido.
    const nextParams = tpl ? Array.from({ length: templateParamCount(tpl.body) }, () => '') : [];
    setParams(nextParams);
    emit(tpl, nextParams);
  };

  const setParam = (index: number, value: string) => {
    const nextParams = params.map((p, i) => (i === index ? value : p));
    setParams(nextParams);
    emit(selected, nextParams);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-select`} className="text-[13px] font-semibold">
          Modelo<span className="-ml-1 text-destructive">*</span>
        </Label>
        <Select
          id={`${idPrefix}-select`}
          className="h-8"
          value={key}
          onChange={(e) => selectTemplate(e.target.value)}
          disabled={disabled || noChannel || templatesQ.isPending || templates.length === 0}
        >
          <option value="">
            {noChannel
              ? 'Escolha o canal primeiro'
              : templatesQ.isPending
                ? 'Carregando modelos…'
                : templates.length === 0
                  ? failures.length > 0
                    ? 'Os modelos deste número não carregaram'
                    : 'Nenhum modelo disponível'
                  : 'Selecione um modelo…'}
          </option>
          {templates.map((t) => (
            <option key={templateKey(t)} value={templateKey(t)}>
              {t.name} · {t.language}
            </option>
          ))}
        </Select>
        {templatesQ.isError ? (
          <p className="text-xs text-destructive" role="alert">
            Erro ao carregar os modelos: {errorMessage(templatesQ.error)}{' '}
            <RetryButton onClick={() => void templatesQ.refetch()} busy={templatesQ.isFetching} />
          </p>
        ) : null}
        {!noChannel && failures.length > 0 ? (
          <div className="text-xs text-destructive" role="alert">
            {failures.map((f) => (
              <p key={f.phoneNumberId}>{templateFailureMessage(f)}</p>
            ))}
            <RetryButton onClick={() => void templatesQ.refetch()} busy={templatesQ.isFetching} />
          </div>
        ) : null}
      </div>

      {templatesQ.isPending ? (
        <div className="flex justify-center py-1">
          <Spinner className="size-4" />
        </div>
      ) : null}

      {selected ? (
        <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-sm leading-7 break-words whitespace-pre-wrap">
          <TemplateBodyPreview
            template={selected}
            params={params}
            onParamChange={setParam}
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}

function RetryButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button
      type="button"
      className="font-medium underline underline-offset-2 disabled:opacity-50"
      onClick={onClick}
      disabled={busy}
    >
      {busy ? 'Tentando…' : 'Tentar de novo'}
    </button>
  );
}

/** Pedaço do body: texto literal ou placeholder {{n}} (número 1-based). */
type BodyPart = { text: string } | { param: number };

/**
 * Split do body pela MESMA gramática de placeholder do servidor
 * (TEMPLATE_PLACEHOLDER_RE de @erp/shared — cobre '{{ 1 }}' com espaços, que
 * o split local antigo rejeitava, deixando o modelo insendável). Independe de
 * flags/grupos de captura do regex compartilhado: itera os matches e extrai o
 * número do token.
 */
function splitTemplateBody(body: string): BodyPart[] {
  const re = new RegExp(TEMPLATE_PLACEHOLDER_RE.source, 'g');
  const parts: BodyPart[] = [];
  let last = 0;
  for (const m of body.matchAll(re)) {
    const index = m.index ?? 0;
    if (index > last) parts.push({ text: body.slice(last, index) });
    parts.push({ param: Number(/\d+/.exec(m[0])?.[0] ?? '0') });
    last = index + m[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

/**
 * Body do modelo com {{n}} substituído por inputs inline — o texto ao redor é
 * o preview vivo. Placeholder fora do range de params (modelo malformado)
 * renderiza literal.
 */
function TemplateBodyPreview({
  template,
  params,
  onParamChange,
  disabled,
}: {
  template: WhatsappHsmTemplate;
  params: string[];
  onParamChange: (index: number, value: string) => void;
  disabled?: boolean;
}) {
  const paramCount = templateParamCount(template.body);
  return (
    <>
      {splitTemplateBody(template.body).map((part, i) => {
        if ('text' in part) return <span key={i}>{part.text}</span>;
        const index = part.param - 1;
        if (index < 0 || index >= paramCount) return <span key={i}>{`{{${part.param}}}`}</span>;
        const value = params[index] ?? '';
        return (
          <input
            key={i}
            type="text"
            value={value}
            onChange={(e) => onParamChange(index, e.target.value)}
            placeholder={`{{${part.param}}}`}
            aria-label={`Parâmetro ${part.param} do modelo`}
            disabled={disabled}
            size={Math.max(6, value.length + 2)}
            className="mx-0.5 inline-block rounded-sm border border-input bg-card px-1.5 py-0.5 align-baseline text-xs shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
          />
        );
      })}
    </>
  );
}
